import { useCallback, useEffect, useRef, useState } from 'react';
import { TURNSTILE_SITE_KEY, loadTurnstile } from './turnstile';
import type { TurnstileApi } from './turnstile';
import { MESSAGES } from './sign-in';

/** How long a submit waits for the challenge before giving up on it. */
const WAIT_MS = 30_000;

/**
 * What a request may carry. `failed` means the challenge errored or never
 * finished: the request is not sent, since the API would only refuse it.
 */
export type CaptchaResult =
  { readonly kind: 'token'; readonly token: string | undefined } | { readonly kind: 'failed' };

export interface Captcha {
  /** Where the widget draws, when Cloudflare needs the person to click. */
  readonly containerRef: (element: HTMLDivElement | null) => void;
  /**
   * A fresh token for one request — `undefined` with no site key. Tokens are
   * single use, so the widget is reset for the next one either way.
   */
  readonly take: () => Promise<CaptchaResult>;
}

type Waiter = (token: string | undefined) => void;

/** The bot check for one sign-in step; `action` is what the API checks it against. */
export function useCaptcha(action: 'code' | 'verify'): Captcha {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const widget = useRef<{ api: TurnstileApi; id: string } | undefined>(undefined);
  const token = useRef<string | undefined>(undefined);
  /** The widget reported an error and has not produced a token since. */
  const errored = useRef(false);
  const waiting = useRef<Waiter[]>([]);

  useEffect(() => {
    if (TURNSTILE_SITE_KEY === '' || container === null) return undefined;
    let gone = false;
    const settle = (value: string | undefined): void => {
      for (const resolve of waiting.current.splice(0)) resolve(value);
    };
    void loadTurnstile()
      .then((api) => {
        if (gone) return;
        const id = api.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          appearance: 'interaction-only',
          'refresh-expired': 'auto',
          callback: (value) => {
            token.current = value;
            errored.current = false;
            settle(value);
          },
          'expired-callback': () => {
            token.current = undefined;
          },
          // A submit waiting on the challenge hears now, not after WAIT_MS.
          'error-callback': () => {
            token.current = undefined;
            errored.current = true;
            settle(undefined);
          },
        });
        widget.current = { api, id };
      })
      .catch(() => {
        // The script itself did not load (blocked, offline): nothing will ever answer.
        errored.current = true;
        settle(undefined);
      });
    return () => {
      gone = true;
      if (widget.current !== undefined) widget.current.api.remove(widget.current.id);
      widget.current = undefined;
      token.current = undefined;
      errored.current = false;
    };
  }, [action, container]);

  const take = useCallback(async (): Promise<CaptchaResult> => {
    if (TURNSTILE_SITE_KEY === '') return { kind: 'token', token: undefined };
    const value = token.current ?? (errored.current ? undefined : await nextToken(waiting.current));
    token.current = undefined;
    errored.current = false;
    if (widget.current !== undefined) widget.current.api.reset(widget.current.id);
    return value === undefined ? { kind: 'failed' } : { kind: 'token', token: value };
  }, []);

  return { containerRef: setContainer, take };
}

/**
 * Runs `send` with a fresh token, or answers the bot-check message without
 * sending when the challenge failed. Shaped for useAttempt: a message or undefined.
 */
export async function withCaptcha(
  captcha: Captcha,
  send: (captchaToken: string | undefined) => Promise<string | undefined>,
): Promise<string | undefined> {
  const result = await captcha.take();
  return result.kind === 'failed' ? MESSAGES.captcha : send(result.token);
}

function nextToken(waiting: Waiter[]): Promise<string | undefined> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      const index = waiting.indexOf(done);
      if (index >= 0) waiting.splice(index, 1);
      resolve(undefined);
    }, WAIT_MS);
    function done(value: string | undefined): void {
      clearTimeout(timer);
      resolve(value);
    }
    waiting.push(done);
  });
}
